import React from 'react';
import { TrendingUp, DollarSign, ShoppingBag, UserCheck, CreditCard, MessageCircleQuestion, BellRing, SearchX } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { ORDER_STATUSES } from '../OrderDetailDrawer';
import SeoQualityCard from '../SeoQualityCard';
import ReviewCoverageCard from '../ReviewCoverageCard';
import EmptyPagesCard from '../EmptyPagesCard';
import OrphanLinksCard from '../OrphanLinksCard';
import ShareAttributionTable from '../ShareAttributionTable';
import CodeHealthCard from '../CodeHealthCard';
import RestockAlert from '../RestockAlert';
import LowStockAlert from '../LowStockAlert';
import LatePurchaseOrdersAlert from '../LatePurchaseOrdersAlert';

const getStatusColor = (status) => {
  switch (status) {
    case 'Livré': return 'bg-green-100 text-green-700';
    case 'Expédié': return 'bg-blue-100 text-blue-700';
    case 'En cours': return 'bg-yellow-100 text-yellow-700';
    case 'Annulée': return 'bg-red-100 text-red-700';
    case 'Remboursée': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const AdminDashboardTab = ({ stats, setStats, orders, setActiveTab, onEditProduct, onNewProduct, onPrepareRestock, openSeoTab, onStatusChange }) => {
  const statCards = [
    { label: 'Chiffre d\'affaires', value: `${stats?.total_revenue?.toFixed(2) || 0} €`, icon: DollarSign, color: 'bg-green-500' },
    { label: 'Commandes', value: stats?.total_orders || 0, icon: ShoppingBag, color: 'bg-blue-500' },
    { label: 'Clients', value: stats?.total_users || 0, icon: UserCheck, color: 'bg-purple-500' },
    { label: 'Produits', value: stats?.total_products || 0, icon: TrendingUp, color: 'bg-orange-500' },
    { label: 'Alertes véhicule', value: stats?.vehicle_alerts_active || 0, icon: BellRing, color: 'bg-sky-600', tab: 'vehicles' },
    { label: 'Plaques à valider', value: stats?.pending_plates || 0, icon: CreditCard, color: 'bg-amber-500', tab: 'plates' },
    { label: 'Questions à répondre', value: stats?.pending_questions || 0, icon: MessageCircleQuestion, color: (stats?.pending_questions || 0) > 0 ? 'bg-red-500' : 'bg-gray-400', tab: 'questions', testid: 'dashboard-pending-questions' },
    { label: 'Recherches sans résultat (7 j)', value: stats?.search_misses_7d || 0, icon: SearchX, color: (stats?.search_misses_7d || 0) > 0 ? 'bg-violet-600' : 'bg-gray-400', tab: 'search-misses', testid: 'dashboard-search-misses' },
  ];
  const reloadStats = () => adminAPI.getStats().then(setStats).catch(() => {});

  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Tableau de bord</h1>

      {stats && <LatePurchaseOrdersAlert items={stats.late_purchase_orders} onReminded={reloadStats} onOpenSuppliers={() => setActiveTab('suppliers')} />}
      {stats && <RestockAlert restock={stats.restock} onPrepare={onPrepareRestock} onLinkSupplier={onEditProduct} />}
      {stats && <LowStockAlert items={stats.low_stock || []} outCount={stats.out_of_stock_count || 0} onViewAll={() => setActiveTab('products')} onEdit={onEditProduct} />}

      <SeoQualityCard onOpen={openSeoTab} />
      <ReviewCoverageCard onOpenSettings={() => setActiveTab('settings')} />
      <EmptyPagesCard onAddProduct={onNewProduct} />
      <OrphanLinksCard onEditProduct={onEditProduct} />
      <ShareAttributionTable compact testId="dashboard-share-attribution" />
      <CodeHealthCard />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        {statCards.map((stat, idx) => (
          <div key={idx} data-testid={stat.tab ? `stat-${stat.tab}` : undefined} onClick={() => stat.tab && setActiveTab(stat.tab)} className={`bg-white rounded-lg shadow-sm p-6 ${stat.tab ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Commandes récentes</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.slice(0, 5).map(order => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-blue-600">{order.id.slice(0, 8)}...</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{order.customer_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{order.total.toFixed(2)} €</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>{order.status}</span>
                    {order.payment_status === 'pending' && <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid={`order-awaiting-payment-${order.id}`}>Paiement en attente</span>}
                  </td>
                  <td className="px-6 py-4">
                    <select value={order.status} onChange={(e) => onStatusChange(order.id, e.target.value)} className="text-sm border rounded px-2 py-1">
                      {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && <p className="text-center py-8 text-gray-500">Aucune commande</p>}
        </div>
      </div>
    </>
  );
};

export default AdminDashboardTab;
